import React from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, ArrowRight, Facebook, Share2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { useContent } from "../context/ContentContext";
import { usePageMeta } from "../hooks/usePageMeta";

const BlogDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { BLOGS } = useContent();
  useReveal([slug, lang]);

  const blog = BLOGS.find((b) => b.slug === slug);
  usePageMeta({ title: blog ? (lang === "en" ? blog.title_en : blog.title_bn) : "Blog", description: blog ? (lang === "en" ? blog.excerpt_en : blog.excerpt_bn) : "", image: blog?.image, type: "article" });
  if (!blog) {
    return (
      <div className="pt-24 pb-20 text-center">
        <p className="text-xl">Article not found.</p>
        <Link to="/blog" className="text-jannah-green underline mt-4 inline-block">Back to blog</Link>
      </div>
    );
  }

  const title = lang === "en" ? blog.title_en : blog.title_bn;
  const body = lang === "en" ? blog.body_en : blog.body_bn;
  const fmt = (d) =>
    new Date(d).toLocaleDateString(lang === "en" ? "en-US" : "bn-BD", {
      year: "numeric", month: "long", day: "numeric",
    });
  const others = BLOGS.filter((b) => b.slug !== slug).slice(0, 2);

  return (
    <div>
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${blog.image})` }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="px-3 py-1 rounded-full btn-gold text-xs font-bold">
            {lang === "en" ? blog.category_en : blog.category_bn}
          </span>
          <h1 className="mt-5 text-3xl md:text-5xl font-bold text-white leading-tight">{title}</h1>
          <div className="mt-5 flex items-center justify-center gap-5 text-white/80 text-sm">
            <span className="flex items-center gap-1.5"><Calendar size={15} /> {fmt(blog.date)}</span>
            <span className="flex items-center gap-1.5"><User size={15} /> {blog.author}</span>
          </div>
        </div>
      </section>

      <section className="section-pad bg-jannah-cream">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-jannah-green font-semibold mb-8 hover:gap-3">
            <ArrowLeft size={17} /> {lang === "en" ? "Back to Blog" : "ব্লগে ফিরে যান"}
          </Link>
          <article className="prose-lg space-y-6">
            {body.map((p, i) => (
              <p key={i} className="text-jannah-green-dark/80 leading-relaxed text-lg">{p}</p>
            ))}
          </article>

          <div className="mt-10 pt-8 border-t border-jannah-sand flex items-center gap-4">
            <span className="text-sm font-semibold text-jannah-green-dark flex items-center gap-2">
              <Share2 size={16} /> {lang === "en" ? "Share" : "শেয়ার"}
            </span>
            <a href="https://www.facebook.com/FLYJANNAH" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-jannah-green text-white flex items-center justify-center hover:bg-jannah-green-dark">
              <Facebook size={16} />
            </a>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 mt-20">
          <h2 className="text-2xl font-bold text-jannah-green-dark mb-6">
            {lang === "en" ? "Related Articles" : "সম্পর্কিত আর্টিকেল"}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {others.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug}`} className="card-hover bg-white rounded-xl overflow-hidden border border-jannah-sand flex">
                <img src={b.image} alt="" className="w-32 h-32 object-cover shrink-0" loading="lazy" />
                <div className="p-4 flex flex-col justify-center">
                  <span className="text-xs font-semibold text-jannah-gold">{lang === "en" ? b.category_en : b.category_bn}</span>
                  <h3 className="mt-1 font-bold text-jannah-green-dark leading-snug line-clamp-2">{lang === "en" ? b.title_en : b.title_bn}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-jannah-green text-sm font-semibold">{t.read_more} <ArrowRight size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
